import { getPeerPublicKey } from './peerKeys'
import { decryptEnvelope, encryptBytes, encryptEnvelope, EncryptedMediaMeta, isEnvelope, SecretPayload } from './e2e'
import { Message } from '@/shared/interfaces/Message'
import { MessageContent } from '@/shared/interfaces/MessageContent'

/** Placeholder shown when a secret message cannot be decrypted on this device. */
export const UNDECRYPTABLE_TEXT = '🔒 Сообщение недоступно на этом устройстве'

export interface EncryptedOutgoing {
  /** JSON envelope to put in the `text` field (holds ciphertext + media keys). */
  envelope: string
  /** Encrypted files to upload as content[i][file], aligned with envelope.media order. */
  files: { file: File; type: string }[]
}

function mediaType(mime: string): string {
  if (/^video\//.test(mime)) return 'video'
  if (/^audio\//.test(mime)) return 'audio'
  if (/^image\//.test(mime) && !mime.includes('svg')) return 'photo'
  return 'document'
}

/**
 * Encrypt an outgoing secret-chat message: file bytes are secretbox-encrypted
 * with per-file random keys, and those keys + the text live inside a crypto_box
 * envelope only the peer can open. The server sees ciphertext only.
 *
 * Throws if the peer has not published an E2E key yet (secret chat impossible).
 */
export async function encryptOutgoingMessage(
  peerUserId: number,
  text: string,
  files: File[],
  keptMedia: EncryptedMediaMeta[] = [],
): Promise<EncryptedOutgoing> {
  const peerPub = await getPeerPublicKey(peerUserId)
  if (!peerPub) {
    throw new Error('E2E_PEER_KEY_MISSING')
  }

  // Правка без новых файлов: вложения остаются на сервере как были, а ключи
  // к ним живут только в конверте — переносим их в новый конверт как есть.
  const media: EncryptedMediaMeta[] = [...keptMedia]
  const outFiles: { file: File; type: string }[] = []

  for (const file of files) {
    const bytes = new Uint8Array(await file.arrayBuffer())
    const { cipher, key, nonce } = encryptBytes(bytes)
    const encFile = new File([cipher], `enc-${media.length}.bin`, { type: 'application/octet-stream' })
    outFiles.push({ file: encFile, type: mediaType(file.type) })
    media.push({
      key,
      nonce,
      mime: file.type || 'application/octet-stream',
      name: file.name,
      size: file.size,
    })
  }

  const payload: SecretPayload = { text }
  if (media.length > 0) payload.media = media

  const envelope = await encryptEnvelope(payload, peerPub)
  return { envelope, files: outFiles }
}

/**
 * Decrypt a secret-chat message in place: replaces the ciphertext envelope in
 * `text` with the plaintext, and attaches per-file secretbox keys to each
 * content item so the media component can fetch + decrypt the bytes.
 * Never throws; on failure the text becomes a neutral placeholder.
 */
export async function decryptIncomingMessage(peerUserId: number, message: Message): Promise<Message> {
  if (!message || typeof message.text !== 'string') return message

  // Reply preview may itself be an encrypted envelope — best-effort decrypt it.
  const answerTo = (message as any).answer_to
  if (answerTo && isEnvelope(answerTo.text)) {
    const peerPubForReply = await getPeerPublicKey(peerUserId)
    if (peerPubForReply) {
      const r = await decryptEnvelope(answerTo.text, peerPubForReply)
      answerTo.text = r ? (r.payload.text ?? '') : UNDECRYPTABLE_TEXT
    }
  }

  if (!isEnvelope(message.text)) return message

  const peerPub = await getPeerPublicKey(peerUserId)
  if (!peerPub) {
    message.text = UNDECRYPTABLE_TEXT
    return message
  }

  const res = await decryptEnvelope(message.text, peerPub)
  if (!res) {
    message.text = UNDECRYPTABLE_TEXT
    return message
  }

  message.text = res.payload.text ?? ''

  const media = res.payload.media
  if (media && Array.isArray(message.content)) {
    for (let i = 0; i < message.content.length; i++) {
      const meta = media[i]
      if (!meta) continue
      message.content[i].secretKey = meta.key
      message.content[i].secretNonce = meta.nonce
      message.content[i].mime = meta.mime
      if (meta.name) message.content[i].name = meta.name
      // Полная запись из конверта нужна при правке сообщения: новый конверт
      // должен нести ключи к тем же файлам, иначе вложение пропадает у обоих.
      message.content[i].secretMeta = { ...meta }
    }
  }

  return message
}

/**
 * Ключи вложений уже расшифрованного сообщения — в порядке его content, как
 * их ждёт конверт. Возвращает null, если хоть к одному файлу ключа на этом
 * устройстве нет: такой конверт перезаписывать нельзя, вложение потеряется.
 */
export function secretMediaOfMessage(message: Message | undefined): EncryptedMediaMeta[] | null {
  const content = message && Array.isArray(message.content) ? message.content : []
  const media: EncryptedMediaMeta[] = []

  for (const item of content) {
    if (item.secretMeta) {
      media.push({ ...item.secretMeta })
      continue
    }
    if (!item.secretKey || !item.secretNonce) return null
    media.push({
      key: item.secretKey,
      nonce: item.secretNonce,
      mime: item.mime || 'application/octet-stream',
      name: item.name,
      size: item.size,
    })
  }

  return media
}

/**
 * Расшифровать отдельную строку из секретного чата (закреплённое сообщение,
 * цитата в списке — всё, что приходит отдельно от самих сообщений).
 * Не бросает исключений: не получилось — вернём понятную заглушку.
 */
export async function decryptSecretText(peerUserId: number, text: string): Promise<string> {
  if (typeof text !== 'string' || !isEnvelope(text)) return text

  const peerPub = await getPeerPublicKey(peerUserId)
  if (!peerPub) return UNDECRYPTABLE_TEXT

  const res = await decryptEnvelope(text, peerPub)

  return res ? (res.payload.text ?? '') : UNDECRYPTABLE_TEXT
}

/** Ссылка на место файла внутри конверта сообщения (приходит в списке медиа). */
export interface SecretMediaRef {
  envelope: string
  index: number
}

/**
 * Список медиа чата (вкладки «Фото», «Файлы» и прочие в информации о чате)
 * приходит без имён файлов и без ключей: и то и другое лежит в конверте
 * сообщения. Достаём их на устройстве и дописываем в элементы списка — дальше
 * работают те же компоненты, что и в самой переписке.
 * Ничего не бросает: что не расшифровалось, остаётся как пришло.
 */
export async function attachSecretMediaMeta(
  peerUserId: number,
  items: { secret?: SecretMediaRef | null; media: MessageContent }[],
): Promise<void> {
  const withSecret = items.filter((item) => item.secret && isEnvelope(item.secret.envelope))
  if (withSecret.length === 0) return

  const peerPub = await getPeerPublicKey(peerUserId)
  if (!peerPub) return

  // Одно сообщение может нести несколько файлов — конверт расшифровываем раз.
  const opened = new Map<string, SecretPayload | null>()

  for (const item of withSecret) {
    const envelope = (item.secret as SecretMediaRef).envelope
    if (!opened.has(envelope)) {
      const res = await decryptEnvelope(envelope, peerPub)
      opened.set(envelope, res ? res.payload : null)
    }

    const meta = opened.get(envelope)?.media?.[(item.secret as SecretMediaRef).index]
    if (!meta) continue

    item.media.secretKey = meta.key
    item.media.secretNonce = meta.nonce
    item.media.mime = meta.mime
    if (meta.name) item.media.name = meta.name
    if (meta.size) item.media.size = meta.size
  }
}

/** Decrypt an array of messages for a secret chat (order preserved). */
export async function decryptChatMessages(peerUserId: number, messages: Message[]): Promise<Message[]> {
  for (const m of messages) {
    await decryptIncomingMessage(peerUserId, m)
  }
  return messages
}
